import { useState } from "react";
import { FileText, FileUp, Paperclip, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DIRECAO_LABELS, nomeDoMes } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { cn } from "@/lib/cn";
import { reais, type NotaDaLista } from "@/lib/dinheiro";
import { NotaDialog } from "./NotaDialog";
import { ImportarNotasDialog } from "./importar/ImportarNotasDialog";

const data = (t: string) => t.split("-").reverse().join("/");

/** Notas do mês (pela data de emissão), com os arquivos e os lançamentos ligados. */
export function NotasFiscais({ competencia }: { competencia: string }) {
  const utils = trpc.useUtils();
  const pode = usePermissao("finance.manage");
  const notas = trpc.financeiro.notas.listar.useQuery({ competencia });
  const [dialogo, setDialogo] = useState<{ editando: NotaDaLista | null } | null>(null);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [importando, setImportando] = useState(false);
  const baixar = trpc.financeiro.notas.baixar.useMutation({ onSuccess: ({ url }) => window.open(url, "_blank", "noopener"), onError: (e) => toast.error(e.message) });
  const excluir = trpc.financeiro.notas.excluir.useMutation({
    onSuccess: () => {
      toast.success("Nota excluída.");
      utils.financeiro.invalidate();
      setConfirmando(null);
    },
    onError: (e) => toast.error(e.message),
  });
  const lista = notas.data ?? [];
  const total = (d: "EMITIDA" | "RECEBIDA") => lista.filter((n) => n.direcao === d).reduce((t, n) => t + n.valorCents, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-zinc-600 dark:text-muted-foreground">
          Emitidas <strong className="tabular-nums text-zinc-900 dark:text-foreground">{reais(total("EMITIDA"))}</strong>
          <span className="mx-2 text-zinc-300">·</span>
          Recebidas <strong className="tabular-nums text-zinc-900 dark:text-foreground">{reais(total("RECEBIDA"))}</strong>
        </p>
        {pode && (
          <div className="flex gap-2">
            <button type="button" onClick={() => setImportando(true)} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted">
              <FileUp className="h-4 w-4" /> Importar notas
            </button>
            <button type="button" onClick={() => setDialogo({ editando: null })} className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700">
              <Plus className="h-4 w-4" /> Registrar nota
            </button>
          </div>
        )}
      </div>

      {notas.data && lista.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 py-12 text-center text-sm text-zinc-500 dark:border-border dark:text-muted-foreground">
          Nenhuma nota emitida em {nomeDoMes(competencia)}.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100 overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:divide-border/50 dark:border-border dark:bg-card">
          {lista.map((n) => (
            <li key={n.id} className="flex flex-wrap items-start gap-3 px-4 py-3">
              <FileText className={cn("mt-0.5 h-5 w-5 shrink-0", n.direcao === "EMITIDA" ? "text-emerald-600" : "text-brand-600")} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-zinc-900 dark:text-foreground">
                  NF {n.numero}
                  {n.serie && <span className="font-normal text-zinc-500"> · série {n.serie}</span>}
                  <span className="ml-2 rounded-full bg-zinc-100 px-2 py-0.5 text-[10.5px] font-bold uppercase text-zinc-600 dark:bg-muted dark:text-muted-foreground">{DIRECAO_LABELS[n.direcao]}</span>
                </p>
                <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                  {data(n.emitidaEm)} · {n.partner?.name ?? n.contraparte ?? "Sem contraparte"}
                  {n.lancamentos.length > 0 && ` · ${n.lancamentos.length} ${n.lancamentos.length === 1 ? "lançamento" : "lançamentos"}`}
                </p>
                {n.arquivos.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {n.arquivos.map((a) => (
                      <button key={a.id} type="button" onClick={() => baixar.mutate({ fileId: a.id })} className="inline-flex items-center gap-1 rounded-md border border-zinc-200 px-2 py-0.5 text-[11px] text-zinc-700 hover:bg-zinc-50 dark:border-border dark:text-foreground/80 dark:hover:bg-muted">
                        <Paperclip className="h-3 w-3" /> {a.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-foreground">{reais(n.valorCents)}</span>
              {pode && (
                <div className="flex items-center gap-1">
                  {confirmando === n.id ? (
                    <>
                      <button type="button" onClick={() => excluir.mutate({ id: n.id })} className="rounded-md bg-red-600 px-2 py-1 text-xs font-semibold text-white hover:bg-red-700">
                        Excluir
                      </button>
                      <button type="button" onClick={() => setConfirmando(null)} className="rounded-md px-2 py-1 text-xs text-zinc-600 hover:bg-zinc-100 dark:hover:bg-muted">
                        Cancelar
                      </button>
                    </>
                  ) : (
                    <>
                      <button type="button" aria-label={`Editar NF ${n.numero}`} onClick={() => setDialogo({ editando: n })} className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 dark:hover:bg-muted">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" aria-label={`Excluir NF ${n.numero}`} onClick={() => setConfirmando(n.id)} className="rounded-md p-1.5 text-zinc-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {pode && <ImportarNotasDialog aberto={importando} onFechar={() => setImportando(false)} />}

      <NotaDialog aberto={!!dialogo} editando={dialogo?.editando ?? null} onFechar={() => setDialogo(null)} />
    </div>
  );
}
